/**
 * SessionProvider component for automatic session restoration
 * Wraps protected routes and handles authentication state restoration
 */

import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/slices/auth-slice'
import { useWorkspaceStore } from '@/store/slices/workspace-slice'
import { api, refreshTokens } from '@/utils/api-client'
import {
  getAccessToken,
  getTokenExpiresAt,
  isRefreshTokenValid,
  isAuthenticated as hasUsableSession,
} from '@/utils/auth-storage'
import LoadingSpinner from './loading-spinner'
// Channel user provider import removed - unified auth system

/**
 * Props for SessionProvider component
 */
interface SessionProviderProps {
  children: React.ReactNode
}

/**
 * Session API response interface
 */


/**
 * SessionProvider component for route protection and session restoration
 * Automatically validates session on page load and handles authentication state
 * Redirects to login with return URL on authentication failure
 * 
 * @param children - Child components to render after session validation
 * @returns React component with session management
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const hasInvitationParams = !!searchParams.get('invitationId')
  const { setUser, setFeatures, setError: setAuthError, isAuthenticated, user } = useAuthStore()
  const { setWorkspaces, setCurrentWorkspace } = useWorkspaceStore()
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  // Track if session has been restored to prevent re-validation on navigation
  const hasRestoredRef = useRef(false)

  useEffect(() => {
    // Skip if session already restored to prevent re-running on navigation
    if (hasRestoredRef.current) {
      return
    }

    // Minimal shape we consume from the /auth/session payload — the server side
    // is still typed as `any` in session.ts, so we narrow structurally here.
    type WorkspaceLike = { id?: string } & Record<string, unknown>
    type SessionPayload = {
      user?: { email?: string } & Record<string, unknown>
      workspace?: WorkspaceLike | null
      workspaces?: WorkspaceLike[] | null
      features?: { projectsEnabled: boolean }
    }

    /**
     * Picks the workspace to set as current, preferring the user's last choice
     * stored per-email in localStorage, falling back to the server's selection,
     * then to the first workspace in the list.
     */
    const pickWorkspace = (
      list: WorkspaceLike[],
      serverSelected: WorkspaceLike | null,
      userEmail: string | undefined
    ): WorkspaceLike | null => {
      if (userEmail) {
        const savedId = localStorage.getItem(`currentWorkspaceId_${userEmail}`)
        if (savedId) {
          const saved = list.find((w) => w?.id === savedId)
          if (saved) return saved
        }
      }
      return serverSelected ?? list[0] ?? null
    }

    /**
     * Applies a /auth/session response payload to the auth + workspace stores.
     * Returns true if the caller should continue rendering children, false if the
     * caller has already navigated away (e.g. to /workspace/create).
     */
    const applySessionPayload = (payload: SessionPayload): boolean => {
      if (payload?.user) {
        setUser(payload.user as Parameters<typeof setUser>[0])
        setAuthError(null)
      }

      // Server-driven feature flags (e.g. Projects). Defaults closed if absent.
      setFeatures(payload?.features ?? { projectsEnabled: false })

      const selectedWorkspace = payload?.workspace ?? null
      const rawList = payload?.workspaces
      // Treat the selected workspace as authoritative — a populated `workspace`
      // means the user has a valid workspace context even if the `workspaces`
      // list came back empty (can happen with stale membership queries).
      const wsList: WorkspaceLike[] =
        Array.isArray(rawList) && rawList.length > 0
          ? rawList
          : selectedWorkspace
          ? [selectedWorkspace]
          : []

      if (wsList.length > 0) {
        setWorkspaces(wsList as Parameters<typeof setWorkspaces>[0])
        const toSet = pickWorkspace(wsList, selectedWorkspace, payload?.user?.email)
        if (toSet) setCurrentWorkspace(toSet as Parameters<typeof setCurrentWorkspace>[0])
        return true
      }

      // No workspaces — either let InvitationHandler accept the invite, or kick
      // the user to the workspace-create flow. Either way, we stop here.
      if (!hasInvitationParams) {
        // Carry the page the user was trying to reach through workspace
        // creation, so it isn't dropped —
        // location.pathname/search is the current same-origin route, safe to
        // reuse directly without the returnUrl-param sanitization.
        const dest = `${location.pathname}${location.search}`
        const target =
          dest && dest !== '/workspace/create'
            ? `/workspace/create?redirectUrl=${encodeURIComponent(dest)}`
            : '/workspace/create'
        navigate(target, { replace: true })
      }
      return false
    }

    /**
     * Restores user session by validating the stored token against /auth/session.
     * Whether or not Zustand already has a persisted `user`, we always fetch the
     * session once per mount — the workspace store isn't persisted, so without
     * this call children would render with an empty workspace context and hit
     * the "No apps found" screen.
     */
    const restoreSession = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // No access token at all → redirect straight to sign-in.
        // api-client would also reject the call below, but short-circuiting here
        // avoids a pointless request and an extra log line.
        const accessToken = getAccessToken()
        if (!accessToken) {
          hasRestoredRef.current = true
          const currentPath = location.pathname + location.search
          const redirectUrl = encodeURIComponent(currentPath)
          navigate(`/signin?redirectUrl=${redirectUrl}`, { replace: true })
          return
        }

        const response = await api.get('/auth/session', {
          requiresAuth: true,
          showErrorToast: false,
        })

        // The server wraps responses in { success, data } — anything else is a
        // bug or a backend regression, and we must not silently render children
        // with no session. Throwing routes through the catch → redirect to /signin.
        if (!response?.success || !response?.data) {
          throw new Error(
            response?.error?.message || 'Session validation returned an invalid payload'
          )
        }

        hasRestoredRef.current = true
        applySessionPayload(response.data as SessionPayload)
      } catch {
        // Mark as restored so the effect doesn't re-enter.
        hasRestoredRef.current = true

        // Clear workspace data on session failure so stale workspace state
        // from a previous session can't leak into the sign-in redirect flow.
        setWorkspaces([])
        setCurrentWorkspace(null)

        // If api-client already kicked off a hard redirect via window.location,
        // this navigate is a no-op. Otherwise it carries the user back to signin
        // with their original URL preserved.
        const currentPath = location.pathname + location.search
        const redirectUrl = encodeURIComponent(currentPath)
        navigate(`/signin?redirectUrl=${redirectUrl}`, { replace: true })
        return
      } finally {
        setIsLoading(false)
      }
    }

    restoreSession()
  }, [navigate, location, setUser, setAuthError, setWorkspaces, setCurrentWorkspace, hasInvitationParams])

  // Set up proactive token expiry checking
  useEffect(() => {
    /**
     * Proper token refresh flow:
     * 1. Check if access token is expired/expiring → attempt refresh
     * 2. Check if refresh token is valid → use it to refresh
     * 3. If refresh token expired → logout
     * 4. Only redirect to signin when refresh token expires or refresh fails
     */
    const checkTokenExpiry = async () => {
      const accessToken = getAccessToken()
      const expiresAt = getTokenExpiresAt()

      // If no tokens at all, redirect to signin
      if (!accessToken) {
        redirectToSignin()
        return
      }

      // Check if refresh token has expired first
      if (!isRefreshTokenValid()) {
        redirectToSignin()
        return
      }

      // Check access token expiry status
      if (!expiresAt) {
        redirectToSignin()
        return
      }

      const timeUntilExpiry = expiresAt - Date.now()
      const warningThreshold = 5 * 60 * 1000 // 5 minute warning for 24-hour tokens

      // If access token is expired or about to expire, attempt refresh
      if (timeUntilExpiry <= warningThreshold) {
        try {
          const refreshResult = await refreshTokens()
          if (refreshResult.success) {
            return // Continue normal operation with new tokens
          }
          // Refresh token may be expired — bounce the user to signin.
          redirectToSignin()
        } catch {
          redirectToSignin()
        }
      }
    }

    /**
     * Helper function to redirect to signin and clear state
     */
    const redirectToSignin = () => {
      // Clear workspace data
      setWorkspaces([])
      setCurrentWorkspace(null)

      // Redirect to signin with current path
      const currentPath = location.pathname + location.search
      const redirectUrl = encodeURIComponent(currentPath)
      navigate(`/signin?redirectUrl=${redirectUrl}`, { replace: true })
    }

    // Only start checking if we have a valid session
    if (isAuthenticated && user && !isLoading) {
      // Check immediately
      checkTokenExpiry()

      // Check every 5 minutes for 24-hour tokens
      tokenCheckIntervalRef.current = setInterval(checkTokenExpiry, 5 * 60 * 1000)
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current)
        tokenCheckIntervalRef.current = null
      }
    }
  }, [isAuthenticated, user, isLoading, navigate, location, setWorkspaces, setCurrentWorkspace])

  // Guard against back/forward cache restores.
  //
  // Back and forward navigation can hand the user a page the browser froze
  // whole — DOM, React tree, stores — and resume it without remounting
  // anything. None of the checks above run on that path: the restore effect is
  // one-shot per mount, and the expiry interval was frozen along with the page.
  // So a page restored this way after signing out is the previous session's
  // private data, back on screen, with no request made that could 401 and
  // dislodge it.
  //
  // Sign-out uses `location.replace`, which keeps the page it left out of
  // history; this covers everything that doesn't reach — deeper history
  // entries, forward navigation, and sessions that lapsed while frozen.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // A normal load already ran every check above; only restores are of interest.
      if (!event.persisted) return

      // Still holding usable credentials — ordinary back navigation, leave it
      // be. An expired access token with a live refresh token counts as usable:
      // api-client refreshes it on the next request.
      if (hasUsableSession()) return

      // A full document load rather than a router navigation, because only a
      // fresh document is guaranteed to be free of the restored heap.
      const currentPath = window.location.pathname + window.location.search
      window.location.replace(`/signin?redirectUrl=${encodeURIComponent(currentPath)}`)
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  // Show loading spinner during session restoration
  if (isLoading) {
    return <LoadingSpinner message="Restoring session..." />
  }

  // Show error state if session restoration failed
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive mb-4">Session Error: {error}</p>
          <button 
            onClick={() => navigate('/signin')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
          >
            Sign In
          </button>
        </div>
      </div>
    )
  }

  // Render children only after successful session validation
  return <>{children}</>
}
